import { useState, useMemo, useCallback } from "react";
import { toastService } from "../../../providers";
import { useToggleMerchantStatus } from "../../../hooks/queries/useMerchantQueries";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";

export const useMerchantTableHandlers = ({ merchants }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingIds, setLoadingIds] = useState(new Set());
  const { mutateAsync: toggleMerchantStatus } = useToggleMerchantStatus();
  const filteredMerchants = useMemo(() => {
    if (!searchTerm) {
      return merchants;
    }
    const term = searchTerm.toLowerCase();
    return merchants.filter((merchant) => {
      return (
        (merchant.merchantName &&
          merchant.merchantName.toLowerCase().includes(term)) ||
        (merchant.merchantId &&
          String(merchant.merchantId).toLowerCase().includes(term)) ||
        (merchant.accountAlias &&
          merchant.accountAlias.toLowerCase().includes(term))
      );
    });
  }, [merchants, searchTerm]);

  const handleToggleStatus = useCallback(
    async (merchant) => {
      const merchantId = merchant.merchantId;
      const isCurrentlyActive = merchant.accountStatus;
      setLoadingIds((prev) => new Set(prev).add(merchantId));
      const actionType = isCurrentlyActive ? "DEACTIVATE" : "ACTIVATE";
      try {
        toastService.info(
          `${actionType === "ACTIVATE" ? "Activating" : "Deactivating"} ${
            merchant.merchantName
          }...`,
        );

        await toggleMerchantStatus({
          merchantId,
          actionType,
        });

        toastService.success(
          `${merchant.merchantName} has been ${
            actionType === "ACTIVATE" ? "activated" : "deactivated"
          } successfully!`,
        );
      } catch (error) {
        toastService.error(
          extractAxiosErrorMessage(
            error,
            `Failed to ${actionType === "ACTIVATE" ? "activate" : "deactivate"} merchant.`,
          ),
        );
      } finally {
        setLoadingIds((prev) => {
          const updated = new Set(prev);
          updated.delete(merchantId);
          return updated;
        });
      }
    },
    [toggleMerchantStatus],
  );

  const noMerchants = merchants.length === 0;
  const noResults = merchants.length > 0 && filteredMerchants.length === 0;

  return {
    searchTerm,
    setSearchTerm,
    filteredMerchants,
    loadingIds,
    handleToggleStatus,
    noMerchants,
    noResults,
  };
};

export default useMerchantTableHandlers;
